import { TZDate, tz } from '@date-fns/tz';
import { addHours, differenceInCalendarDays, differenceInSeconds, format } from 'date-fns';

import type { PublicRoleCard } from '../projections.ts';
import { REGION_TAGS } from '../regions.ts';
import { COMPANY_STAGE_LABELS, REGION_TAG_LABELS } from './labels.ts';

// Every date and time the apps show is formatted here with date-fns (an ESLint
// rule bans `toLocale{Date,Time}String` and `Intl.DateTimeFormat` elsewhere).
// date-fns carries its own month and weekday names, so local, CI and Workers
// print the same thing whatever their ICU version ("Sep", never "Sept").
// Dates and times are in the runtime's zone: the viewer's own in the browser,
// UTC wherever we render on the server (Astro pages, SSR of hydrated islands;
// islands must defer local dates until hydrated). Cron schedules are always
// labelled in UTC (jobs.ts, fmtUtc*). See Q60.

const UTC = tz('UTC');

/** Whole calendar days from `iso` to `now` (local time): 0 = today, 1 = yesterday. */
export function calendarDaysAgo(iso: string, now = new Date()): number {
  return differenceInCalendarDays(now, iso);
}

/** "Today at 11:46", "Yesterday at 09:05", else "12 Sep at 14:30" (local time). */
export function fmtDayTime(iso: string, now = new Date()): string {
  const days = calendarDaysAgo(iso, now);
  if (days === 0) return format(iso, "'Today at' HH:mm");
  if (days === 1) return format(iso, "'Yesterday at' HH:mm");
  return format(iso, "d MMM 'at' HH:mm");
}

/** Dense tables: "Today, 11:46", "Yesterday", "3 days ago", else "12 Sep 2026". */
export function fmtRecent(iso: string, now = new Date()): string {
  const days = calendarDaysAgo(iso, now);
  if (days === 0) return format(iso, "'Today,' HH:mm");
  if (days === 1) return 'Yesterday';
  if (days > 1 && days < 7) return `${days} days ago`;
  return fmtDate(iso);
}

/** "12 Sep 2026" (local time); "—" when unset. */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return format(iso, 'd MMM yyyy');
}

/** "today", "yesterday", "3 days ago", else "12 Sep" (local time). */
export function relativeDate(iso: string, now = new Date()): string {
  const days = calendarDaysAgo(iso, now);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  return format(iso, 'd MMM');
}

/** "today", "yesterday", else "29 Sep" (local time). */
export function shortDay(iso: string, now = new Date()): string {
  const days = calendarDaysAgo(iso, now);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  return format(iso, 'd MMM');
}

/** The local calendar day, "2026-09-29", for grouping events by day. */
export function dayKey(iso: string): string {
  return format(iso, 'yyyy-MM-dd');
}

/** "Saturday 3 Oct 2026" (local time). */
export function fmtWeekdayDate(iso: string): string {
  return format(iso, 'EEEE d MMM yyyy');
}

/**
 * The ISO instant `days` × 24h after `iso`. Exact hours, not calendar days, to
 * match the server's TTL maths (e.g. consent token `expires_at`) across DST.
 */
export function addDaysIso(iso: string, days: number): string {
  return addHours(iso, days * 24).toISOString();
}

/** A job run's length, "42s" or "3m 5s"; null while it is still running. */
export function fmtDuration(start: string, end: string | null): string | null {
  if (!end) return null;
  const s = Math.max(0, differenceInSeconds(end, start));
  return s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`;
}

/** A cron run's clock time, in UTC like its schedule: "03:00 UTC". */
export function fmtUtcTime(iso: string): string {
  return `${format(iso, 'HH:mm', { in: UTC })} UTC`;
}

/**
 * A cron run's time with its day, both in UTC: "today at 03:00 UTC",
 * "yesterday at 03:00 UTC", "on 29 Sep at 03:00 UTC".
 */
export function fmtUtcDayTime(iso: string, now = new Date()): string {
  const days = differenceInCalendarDays(now, iso, { in: UTC });
  const time = fmtUtcTime(iso);
  if (days === 0) return `today at ${time}`;
  if (days === 1) return `yesterday at ${time}`;
  return `on ${format(iso, 'd MMM', { in: UTC })} at ${time}`;
}

/**
 * What time it is right now for someone in this time zone, like "14:05".
 * Returns null if the time zone name isn't valid.
 */
export function timeIn(timeZone: string, at = new Date()): string | null {
  try {
    return format(new TZDate(at, timeZone), 'HH:mm');
  } catch {
    return null;
  }
}

/** The browser's own IANA zone. Client only: on the server it is the server's (UTC). */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return '';
  }
}

/** File sizes: "212 KB", "1.4 MB". */
export function fileSizeLabel(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** "1 match", "3 matches": the count plus the right noun. */
export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

export function salaryLabel(
  role: Pick<PublicRoleCard, 'salaryMin' | 'salaryMax' | 'currency'>,
): string | null {
  const { salaryMin, salaryMax, currency } = role;
  if (salaryMin === null && salaryMax === null) return null;
  const fmt = (n: number) =>
    new Intl.NumberFormat('en', {
      style: currency ? 'currency' : 'decimal',
      currency: currency ?? undefined,
      maximumFractionDigits: 0,
    }).format(n);
  if (salaryMin !== null && salaryMax !== null) return `${fmt(salaryMin)} – ${fmt(salaryMax)}`;
  if (salaryMin !== null) return `from ${fmt(salaryMin)}`;
  return `up to ${fmt(salaryMax!)}`;
}

/** Compact range for dense UI: "USD 70k–90k", "from 70k", "up to 1.2m". */
export function salaryShortLabel(
  role: Pick<PublicRoleCard, 'salaryMin' | 'salaryMax' | 'currency'>,
): string | null {
  const { salaryMin, salaryMax, currency } = role;
  if (salaryMin === null && salaryMax === null) return null;
  const fmt = (n: number) =>
    new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 })
      .format(n)
      .toLowerCase();
  const range =
    salaryMin !== null && salaryMax !== null
      ? `${fmt(salaryMin)}–${fmt(salaryMax)}`
      : salaryMin !== null
        ? `from ${fmt(salaryMin)}`
        : `up to ${fmt(salaryMax!)}`;
  return currency ? `${currency} ${range}` : range;
}

export function overlapLabel(overlapHours: number | null): string | null {
  if (overlapHours === null) return null;
  return `${overlapHours}h overlap`;
}

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

export function countryName(code: string): string {
  try {
    return regionNames.of(code) ?? code;
  } catch {
    return code;
  }
}

export function regionLabel(code: string): string {
  return (REGION_TAG_LABELS as Record<string, string>)[code] ?? countryName(code);
}

/** Region tags stay as their code ("EU", "APAC"); countries get their name. */
export function regionShortLabel(code: string): string {
  if (code === 'WORLDWIDE') return 'Worldwide';
  return (REGION_TAGS as readonly string[]).includes(code) ? code : countryName(code);
}

/** Company stage column is a plain varchar; unknown values show as stored. */
export function companyStageLabel(stage: string): string {
  return (COMPANY_STAGE_LABELS as Record<string, string>)[stage] ?? stage;
}

/** "1-10" → "1–10". */
export function companySizeLabel(band: string): string {
  return band.replace('-', '–');
}

export function regionsLabel(regions: string[]): string {
  if (regions.length === 0) return 'Worldwide';
  return regions.map(regionLabel).join(', ');
}

/**
 * First name for greetings; "there" when unknown. Matches the original
 * `fullName.split(' ')[0] ?? 'there'` exactly: splits on single spaces only
 * and returns '' (not 'there') for an empty string — do not "improve" this
 * without checking every call site's expectations (REFACTOR_PLAN.md 3.8).
 */
export function firstName(fullName: string): string {
  return fullName.split(' ')[0] ?? 'there';
}
